import React, { useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import { proxyAPI } from "../proxyAPI";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import PageHeader from "../Common/PageHeader";
import ApiIcon from "../../assets/icons/prod_api.svg2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faX } from "@fortawesome/free-solid-svg-icons";
import Input from "../Builder/nodes/Logic/Inputs/Input";
import { categories, industryTypes } from "../helpers/categories";

export interface ApiProfileType {
  Uuid: string;
  Label: string;
  ClientId: number;
  BaseUrl: string;
  Authenticate: "Basic" | "Bearer";
  AuthHeader: string;
  AuthValue: string;
  Timeout: number;
  Category: string;
  Industry: string;
  DefaultMethod: "GET" | "POST" | "PUT" | "DELETE";
  QueryParameters: {
    Key: string;
    Value: string;
  }[];
  PostFormParameters: {
    Key: string;
    Value: string;
  }[];
  PostJSON: string;
  JsonPayload: any;
}

export default function ApiProfile() {
  const { apiProfileUUID } = useParams();
  const navigate = useNavigate();
  const [jsonText, setJsonText] = useState("");
  const [apiProfile, setApiProfile] = useState<ApiProfileType>({
    Uuid: "",
    Label: "",
    ClientId: 0,
    BaseUrl: "",
    Authenticate: "Basic",
    AuthHeader: "",
    AuthValue: "",
    Timeout: 5,
    DefaultMethod: "GET",
    QueryParameters: [],
    PostFormParameters: [],
    PostJSON: "",
    JsonPayload: null,
    Category: "",
    Industry: "",
  });
  // const [fields, setFields] = useState<ApiDataType[]>([]);
  const [selected, setSelected] = useState<number>();
  const [jsonError, setJsonError] = useState<string>();
  const [testJSON, setTestJSON] = useState({ data: "" });

  const saveApiProfile = async () => {
    const res = await proxyAPI({
      prefix: "logic/workflows/api",
      url: "api-profile",
      method: "POST",
      body: apiProfile,
    });
    if (res) {
      toast.success("API Profile saved successfully");
      navigate("/library/api-profiles/" + res.Uuid);
    } else {
      toast.error("Error saving API Profile");
    }
  };

  const getApiProfile = async () => {
    const res = await proxyAPI({
      prefix: "logic/workflows/api",
      url: "api-profile/" + apiProfileUUID,
      method: "GET",
    });
    if (res) {
      setApiProfile(res);
    }
  };

  const getExampleJSON = async () => {
    const res = await proxyAPI({
      prefix: "logic/workflows/api",
      url: "api-try",
      method: "POST",
      body: { API: apiProfile },
    });
    if (res) {
      setTestJSON(res);
      toast.success("API example JSON received successfully");
    } else {
      toast.error("Error fetching API example JSON");
    }
  };

  useEffect(() => {
    if (apiProfileUUID) {
      getApiProfile();
    }
  }, [apiProfileUUID]);

  return (
    <div className="mb-5">
      <PageHeader
        heading="API Profile"
        subheading={""}
        children={undefined}
        Icon={ApiIcon}
      />
      <div className="row mb-3 bg-white rounded-3 p-3 py-2 ps-3 pe-5 me-3">
        <h5>API Profile Options</h5>
        <div className="mb-3 col-4">
          <label className="form-label">Label</label>
          <input
            type="text"
            className="form-control"
            value={apiProfile.Label}
            onChange={(e) =>
              setApiProfile({
                ...apiProfile,
                Label: e.target.value,
              })
            }
          />
        </div>
        <div className="mb-3 col-4">
          <label className="form-label">Base URL</label>
          <input
            type="text"
            className="form-control"
            value={apiProfile.BaseUrl}
            onChange={(e) =>
              setApiProfile({
                ...apiProfile,
                BaseUrl: e.target.value,
              })
            }
          />
        </div>
        <div className="mb-3 col-4">
          <label className="form-label">Auth Header</label>
          <input
            type="text"
            className="form-control"
            value={apiProfile.AuthHeader}
            onChange={(e) =>
              setApiProfile({
                ...apiProfile,
                AuthHeader: e.target.value,
              })
            }
          />
        </div>
        <div className="mb-3 col-4">
          <label className="form-label">Auth Value</label>
          <input
            type="text"
            className="form-control"
            value={apiProfile.AuthValue}
            onChange={(e) =>
              setApiProfile({
                ...apiProfile,
                AuthValue: e.target.value,
              })
            }
          />
        </div>
        <div className="mb-3 col-4">
          <label className="form-label">Timeout</label>
          <input
            type="number"
            className="form-control"
            value={apiProfile.Timeout}
            onChange={(e) =>
              setApiProfile({
                ...apiProfile,
                Timeout: parseInt(e.target.value),
              })
            }
          />
        </div>
        <div className="mb-3 col-4"></div>

        <div className="mb-3 col-4">
          <label className="form-label">Default Method</label>
          <select
            className="form-select"
            value={apiProfile.DefaultMethod}
            onChange={(e) =>
              setApiProfile({
                ...apiProfile,
                DefaultMethod: e.target.value as
                  | "GET"
                  | "POST"
                  | "PUT"
                  | "DELETE",
              })
            }
          >
            <option value="GET">GET</option>
            <option value="POST">POST</option>
            <option value="PUT">PUT</option>
            <option value="DELETE">DELETE</option>
          </select>
        </div>
        <div className="mb-3 col-4">
          {/* <HelpButton helpKey={} /> */}
          <div>
            <label className="form-label">Industry</label>
          </div>
          <div>
            <Input
              inputType="select"
              isValidNewOption={(o) => o}
              value={[apiProfile?.Industry || ""]}
              onChange={(values: any) =>
                setApiProfile((prev) => ({
                  ...prev,
                  Industry: values[0],
                }))
              }
              options={[
                {
                  value: "",
                  label: `Default from product settings`,
                },
                ...industryTypes,
              ]}
            />
          </div>
        </div>
        <div className="mb-3 col-4">
          {/* <HelpButton helpKey={} /> */}
          <div>
            <label className="form-label">Category</label>
          </div>
          <Input
            inputType="select"
            isValidNewOption={(o) => o}
            value={[apiProfile?.Category || ""]}
            onChange={(values: any) =>
              setApiProfile((prev) => ({
                ...prev,
                Category: values[0],
              }))
            }
            options={[
              {
                value: "",
                label: `Default from product settings`,
              },
              ...categories,
            ]}
          />
        </div>
      </div>

      <div className="mb-3 row d-flex justify-content-between bg-white rounded-3 p-3 py-2 ps-3 pe-5 me-3">
        <h5 className="col-6">Query Parameters</h5>
        <Button
          className="col-2 rounded-pill px-5"
          variant="colored"
          onClick={() => {
            setApiProfile({
              ...apiProfile,
              QueryParameters: [
                ...(apiProfile.QueryParameters || []),
                { Key: "", Value: "" },
              ],
            });
          }}
        >
          Add Query Parameter
        </Button>
      </div>
      {
        // Query Parameters
        apiProfile.QueryParameters?.map((param, index) => (
          <div className="mb-3 row align-items-end" key={index}>
            <div className="col-6">
              <label className="form-label">Key</label>
              <input
                type="text"
                className="form-control"
                value={param.Key}
                onChange={(e) => {
                  const newParams = [...apiProfile.QueryParameters];
                  newParams[index].Key = e.target.value;
                  setApiProfile({
                    ...apiProfile,
                    QueryParameters: newParams,
                  });
                }}
              />
            </div>
            <div className="col-5">
              <label className="form-label">Value</label>
              <input
                type="text"
                className="form-control"
                value={param.Value}
                onChange={(e) => {
                  const newParams = [...apiProfile.QueryParameters];
                  newParams[index].Value = e.target.value;
                  setApiProfile({
                    ...apiProfile,
                    QueryParameters: newParams,
                  });
                }}
              />
            </div>
            <div className="col-1">
              <Button
                variant="danger"
                className="w-100"
                onClick={() => {
                  const newParams = [...apiProfile.QueryParameters];
                  newParams.splice(index, 1);
                  setApiProfile({
                    ...apiProfile,
                    QueryParameters: newParams,
                  });
                }}
              >
                Remove
              </Button>
            </div>
          </div>
        ))
      }

      <div className="mb-3 row d-flex justify-content-between py-2 ps-3 pe-5 me-3 bg-white p-3 rounded-3">
        <h5 className="col-6">Post Form Parameters</h5>
        <Button
          className="col-2 rounded-pill px-5"
          variant="colored"
          onClick={() => {
            setApiProfile({
              ...apiProfile,
              PostFormParameters: [
                ...(apiProfile.PostFormParameters || []),
                { Key: "", Value: "" },
              ],
            });
          }}
        >
          Add Form Parameter
        </Button>
      </div>
      {
        // Post Form Parameters
        apiProfile.PostFormParameters?.map((param, index) => (
          <div className="mb-3 row align-items-end" key={index}>
            <div className="col-6">
              <label className="form-label">Key</label>
              <input
                type="text"
                className="form-control"
                value={param.Key}
                onChange={(e) => {
                  const newParams = [...apiProfile.PostFormParameters];
                  newParams[index].Key = e.target.value;
                  setApiProfile({
                    ...apiProfile,
                    PostFormParameters: newParams,
                  });
                }}
              />
            </div>
            <div className="col-5">
              <label className="form-label">Value</label>
              <input
                type="text"
                className="form-control"
                value={param.Value}
                onChange={(e) => {
                  const newParams = [...apiProfile.PostFormParameters];
                  newParams[index].Value = e.target.value;
                  setApiProfile({
                    ...apiProfile,
                    PostFormParameters: newParams,
                  });
                }}
              />
            </div>
            <div className="col-1">
              <Button
                variant="danger"
                className="w-100"
                onClick={() => {
                  const newParams = [...apiProfile.PostFormParameters];
                  newParams.splice(index, 1);
                  setApiProfile({
                    ...apiProfile,
                    PostFormParameters: newParams,
                  });
                }}
              >
                Remove
              </Button>
            </div>
          </div>
        ))
      }

      <div className="position-relative mb-3 bg-white p-3 row rounded-3 py-2 ps-3 pe-5 me-3 ">
        <h5>Post JSON</h5>
        <textarea
          className="form-control"
          value={apiProfile.PostJSON}
          onChange={(e) => {
            try {
              setJsonError(undefined);
              JSON.parse(e.target.value);
            } catch (err) {
              if (e.target.value !== "") {
                setJsonError("Invalid JSON");
              }
            }
            setApiProfile({
              ...apiProfile,
              PostJSON: e.target.value,
            });
          }}
        />
        {jsonError && (
          <div className="position-absolute z-1 w-100 alert alert-warning my-1">
            {jsonError}
          </div>
        )}
      </div>

      <div className="d-flex justify-content-end my-5 gap-1 me-3">
        <Button
          className="rounded-pill px-3"
          variant="colored"
          onClick={getExampleJSON}
        >
          Test
        </Button>
        <Button
          variant="colored"
          className="rounded-pill px-4"
          onClick={saveApiProfile}
        >
          Save API Profile
        </Button>
      </div>

      {testJSON.data && (
        <div className="mb-3">
          <button
            className="btn float-end"
            onClick={() => setTestJSON({ data: "" })}
          >
            <FontAwesomeIcon icon={faX} />
          </button>
          <h5>Example JSON</h5>
          <textarea
            className="form-control"
            style={{ height: 300 }}
            value={testJSON.data}
            readOnly
          />
        </div>
      )}
    </div>
  );
}
